import React, {useCallback, useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import LazyImage from "../../app-specific/lazy-image";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import {useTheme} from "@mui/material";
import {Close} from "@mui/icons-material";
import IconButton from "../../base/button/icon_button";
import {ReactComponent as AssignOperatorIcon} from "../../../../assets/images/assign-button-icon.svg";
import {ReactComponent as DueDateIcon} from "../../../../assets/images/due-date-icon.svg";
import {DatePicker} from "@mui/x-date-pickers";
import AssignOperatorsPopover from "./assign-operators-popover";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {MuiRFF, Yup} from '../../form'
import DateUtils from "../../../../core/services/date-utils";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import {numComparator} from "../../../../core/services/utils/utils";
import CacheService from "../../../../core/services/cache/cache_service";


const formNames = {
    title: 'title',
    description: 'description',
    dueDate: 'dueDate',
    completionDate: 'completionDate'
}

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.description]: Yup.string().trim().nullable(),
    [formNames.dueDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .nullable(),
    [formNames.completionDate]: Yup.mixed()
        .validDate(ValidateMessages.incorrect)
        .nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const UpsertTaskDialog = () => {
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.upsertTask] ?? {});
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [assignedOperators, _setAssignedOperators] = useState([]);
    const [completionDatePickerOpen, setCompletionDatePickerOpen] = useState(false);
    const [dueDatePickerOpen, setDueDatePickerOpen] = useState(false);
    const [statuses, setStatuses] = useState([]);
    const [task, setTask] = useState({});

    const dispatch = useDispatch();
    const theme = useTheme();
    const isMounted = useIsMounted()

    const anchorTargetRef = useRef(null);
    const anchorTargetRefCompletion = useRef(null);


    /**
     * Sets the assigned operators in the inner state
     * @type {(function(*): void)}
     */
    const setAssignedOperators = useCallback((callbackOrData) => {
        _setAssignedOperators(prevState => {
            let newState;
            if (typeof callbackOrData === 'function')
                newState = callbackOrData(prevState);
            else
                newState = callbackOrData;
            // only accept the first operator
            return newState.slice(0, 1);
        })
    }, []);

    /**
     * With each change in the dialog being open and the task
     * updates the [task] and [initialValues] the inner state
     */
    useEffect(() => {
        setTask(state?.task);
        if (!state.open) {
            setInitialValues({
                [formNames.title]: undefined,
                [formNames.description]: undefined,
                [formNames.completionDate]: null,
                [formNames.dueDate]: null,
            });
            setAssignedOperators([]);
            return;
        }
        setInitialValues({
            [formNames.title]: state?.task?.title ?? undefined,
            [formNames.description]: state?.task?.description ?? undefined,
            [formNames.dueDate]: state?.task?.dueDate ? DateUtils.serverToUTC(state?.task?.dueDate) : undefined,
            [formNames.completionDate]: state?.task?.completionDate ? DateUtils.serverToUTC(state?.task?.completionDate) : undefined,
        })

        setAssignedOperators(state?.task?.assignedToOperator
            ? [{
                firstName: state?.task?.assignedToOperator?.firstname,
                lastName: state?.task?.assignedToOperator?.lastname,
                id: state?.task?.assignedToOperator?.id,
            }]
            : []
        );
    }, [setAssignedOperators, state.open, state?.task])

    /**
     * Closes the dialog by dispatching new state for the task more info dialog
     */
    const closeDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertTask, {open: false}));
    }

    /**
     * Submits the changes and calls the onSuccess method of the redux state
     */
    const onSubmit = async (values) => {
        setLoading(true);
        /**@type {ApiResponseDs}*/
        let response;
        if (!!state?.task?.id) {
            // edit
            const forApi = {
                id: state.task.id,
                title: values.title,
                description: values.description ?? '',
                assignedToOperator: assignedOperators?.[0] ? {
                    firstname: assignedOperators[0].firstName,
                    lastname: assignedOperators[0].lastName,
                    id: assignedOperators[0].id,
                } : null,
                dueDate: values.dueDate ? DateUtils.UTCToServer(values.dueDate).format(DateUtils.withoutOffsetFormat) : undefined,
                ...(state.isNCRTask ? {completionDate: values.completionDate ? DateUtils.UTCToServer(values.dueDate).format(DateUtils.withoutOffsetFormat) : undefined} : {}),
                ...(state?.task?.isStatusEditable ? {taskStatus: task?.taskStatus} : {}),
                updatedProperties: [
                    'Title',
                    'Description',
                    'AssignedToOperator',
                    'DueDate',
                    ...(state.isNCRTask ? ['CompletionDate'] : []),
                    ...(state?.task?.isStatusEditable ? ['Status'] : [])
                ]
            }
            response = await Api.updateTask(forApi);
        } else {
            // create
            const forApi = {
                title: values.title,
                description: values.description ?? '',
                assignedToOperator: assignedOperators?.[0] ? {
                    firstname: assignedOperators[0].firstName,
                    lastname: assignedOperators[0].lastName,
                    id: assignedOperators[0].id,
                } : null,
                dueDate: values.dueDate ? DateUtils.UTCToServer(values.dueDate).format(DateUtils.withoutOffsetFormat) : undefined,
                completionDate: values.completionDate ? DateUtils.UTCToServer(values.dueDate).format(DateUtils.withoutOffsetFormat) : undefined,
                tagList: state.tags,
            }
            response = await Api.createTask(forApi);

        }
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            closeDialog();
            state?.onSuccess?.(response.data);
        }
    }

    /**
     * Toggles date picker being open
     * @param {boolean} shouldOpen
     */
    const toggleDueDatePickerVisibility = (shouldOpen) => {
        setDueDatePickerOpen(shouldOpen);
    }


    /**
     * Toggles date picker being open
     * @param {boolean} shouldOpen
     */
    const toggleCompletionDatePickerVisibility = (shouldOpen) => {
        setCompletionDatePickerOpen(shouldOpen);
    }


    /**
     * Fetches the task statuses from the api
     * @type {(function(): Promise<void>)}
     */
    const getStatuses = useCallback(async () => {
        const response = await Api.getTaskStatuses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const statuses = response?.data?.sort((a, b) => numComparator(a.orderIndex, b.orderIndex)) ?? [];
            setStatuses(statuses);
        }
    }, [isMounted]);

    /**
     * Fetches the statuses on dialog open.
     */
    useEffect(() => {
        if (state.open) {
            void getStatuses();
        }
    }, [getStatuses, state.open]);


    /**
     * Handles the status update
     * @param {event} event
     * @type {(function(*): void)|*}
     */
    const handleStatusUpdate = useCallback((event) => {
        const newStatusId = event.target.value;
        const updateTask = {
            ...task,
            taskStatus: {
                id: newStatusId,
                title: statuses.find(status => status.id === newStatusId)?.title,
                statusType: statuses.find(status => status.id === newStatusId)?.statusType,
            },
        };
        setTask(updateTask);
    }, [statuses, task])


    return (
        <Dialog
            open={state?.open}
            setOpen={closeDialog}
            maxWidth={'sm'}
            className={'upsert-task-dialog'}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        {
                            state?.title ?? (
                                !!state?.task?.id
                                    ? 'Edit Task'
                                    : 'Add a New Task'
                            )
                        }
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className='no-gutters justify-content-around w-85'>
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedInputForm
                                            label={'Title'}
                                            name={formNames.title}
                                            type={'text'}
                                            required={required[formNames.title]}
                                            value={values[formNames.title] ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    {
                                        state?.task?.isStatusEditable && !!state?.task?.id &&
                                        <Col xs={12} className={'mb-3'}>
                                            <OutlinedDropdownForm
                                                label={'Status'}
                                                name={`status`}
                                                disabled={false}
                                                onChange={handleStatusUpdate}
                                                required={"status"}
                                                value={task?.taskStatus?.id || ''}
                                                renderValue={taskStatus => {
                                                    const selectedStatus = statuses.find(s => s.id === taskStatus);
                                                    return selectedStatus ? selectedStatus.title : " ";
                                                }}
                                                data={statuses?.map(e => ({
                                                    value: e.id,
                                                    label: e.title,
                                                    id: e.id
                                                })) ?? []}
                                            />
                                        </Col>
                                    }
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedInputForm
                                            label={'Description'}
                                            name={formNames.description}
                                            type={'text'}
                                            required={required[formNames.description]}
                                            value={values[formNames.description] ?? ''}
                                            minRows={5}
                                            multiline
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div ref={anchorTargetRef} className={'row no-gutters justify-content-start w-85'}>
                                    <div
                                        className={'assign-operator-button'}
                                        onClick={() => setAnchorEl(anchorTargetRef.current)}
                                    >
                                        {
                                            assignedOperators.length === 0
                                                ? <AssignOperatorIcon className={'assign-icon'}/>
                                                : assignedOperators.length === 1
                                                    ? <div className={'assigned-operator-image'}>
                                                        <LazyImage
                                                            alt={'profile'}
                                                            noImage={<div style={{backgroundColor: `${theme.palette.background.default}`}}>
                                                                <ImagePlaceholder/></div>}
                                                            src={CacheService.getOperatorProfileImage(assignedOperators[0]?.id)}
                                                            height={28}
                                                        />
                                                    </div>
                                                    : <div className={'assigned-operators-image-group'}>
                                                        {
                                                            assignedOperators.slice(0, Math.min(assignedOperators.length, 3)).map((e, index) => (
                                                                <div
                                                                    className={'assigned-operator-image'}
                                                                    style={{marginLeft: index !== 0 ? '-13px' : "unset"}}
                                                                >
                                                                    <LazyImage
                                                                        alt={'profile'}
                                                                        noImage={<div
                                                                            style={{backgroundColor: `${theme.palette.background.default}`}}>
                                                                            <ImagePlaceholder/></div>}
                                                                        src={CacheService.getOperatorProfileImage(e?.id)}
                                                                        height={28}
                                                                    />
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                        }
                                        {
                                            assignedOperators.length === 0 ? <p>Assign to</p>
                                                : assignedOperators.length === 1
                                                    ? <p>{`${assignedOperators[0]?.firstName} ${assignedOperators[0]?.lastName}`}</p>
                                                    : assignedOperators.length > 3
                                                        ? <p>+3</p>
                                                        : <p>{assignedOperators.length}</p>
                                        }
                                    </div>
                                    <AssignOperatorsPopover
                                        loading={loading}
                                        anchorEl={anchorEl}
                                        setAnchorEl={setAnchorEl}
                                        assignedOperators={assignedOperators}
                                        setAssignedOperators={setAssignedOperators}
                                    />
                                    <DatePicker
                                        slotProps={{
                                            popper: {
                                                anchorEl: anchorTargetRef.current,
                                            },
                                        }}
                                        disablePast
                                        open={dueDatePickerOpen}
                                        onClose={() => toggleDueDatePickerVisibility(false)}
                                        value={values.dueDate ?? ""}
                                        onChange={(date) => form.change(formNames.dueDate, date)}
                                        slots={{
                                            textField: (_props) => {
                                                const props = {..._props};
                                                delete props.ownerState;
                                                delete props.inputProps;
                                                delete props.InputProps;
                                                delete props.focused;
                                                delete props.error;
                                                delete props.sx;
                                                return (

                                                    <div
                                                        {...props}
                                                        className={'assign-operator-button ml-2'}
                                                        onClick={() => toggleDueDatePickerVisibility(true)}
                                                    >
                                                        <DueDateIcon className={'assign-icon'}/>
                                                        <p>
                                                            {
                                                                !!values.dueDate
                                                                    ? DateUtils.serverToLocal(values.dueDate).format(DateUtils.dateFormat)
                                                                    : 'Due Date'
                                                            }
                                                        </p>
                                                        {
                                                            !!values.dueDate &&
                                                            <IconButton
                                                                onPressed={(e) => e.preventDefault() || e.stopPropagation() || form.change(formNames.dueDate, null)}
                                                                color={'secondary'}
                                                                className={'ml-2'}
                                                            >
                                                                <Close className='icon extraSmall'/>
                                                            </IconButton>
                                                        }
                                                    </div>
                                                )
                                            }
                                        }}
                                    />
                                    {
                                        state?.isNCRTask &&
                                        <DatePicker
                                            slotProps={{
                                                popper: {
                                                    anchorEl: anchorTargetRefCompletion.current,
                                                },
                                            }}
                                            disablePast
                                            open={completionDatePickerOpen}
                                            onClose={() => toggleCompletionDatePickerVisibility(false)}
                                            value={values.completionDate}
                                            onChange={(date) => form.change(formNames.completionDate, date)}
                                            slots={{
                                                textField: (props) => (
                                                    <div
                                                        {...props}
                                                        className={'assign-operator-button ml-2'}
                                                        onClick={() => toggleCompletionDatePickerVisibility(true)}
                                                    >
                                                        <DueDateIcon className={'assign-icon'}/>
                                                        <p>
                                                            {
                                                                !!values.completionDate
                                                                    ? DateUtils.serverToLocal(values.completionDate).format(DateUtils.dateFormat)
                                                                    : 'Completion Date'
                                                            }
                                                        </p>
                                                        {
                                                            !!values.completionDate &&
                                                            <IconButton
                                                                onPressed={(e) => e.preventDefault() || e.stopPropagation() || form.change(formNames.completionDate, null)}
                                                                color={'secondary'}
                                                                className={'ml-2'}
                                                            >
                                                                <Close className='icon extraSmall'/>
                                                            </IconButton>
                                                        }
                                                    </div>
                                                )
                                            }}
                                        />
                                    }
                                </div>
                                <div className='my-3 w-100'/>
                                <Divider className='divider'/>
                                <Row className='no-gutters justify-content-between align-items-start w-100'>
                                    <Button
                                        disabled={!state?.open || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={closeDialog}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        {loading ? 'loading...' : 'Close'}
                                    </Button>
                                    <Button
                                        disabled={!state.open || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        type={'submit'}
                                        borderRadius={'8px 0 8px 0'}
                                    >
                                        {loading ? 'loading...' : !!state?.task?.id ? 'Edit' : 'Create'}
                                    </Button>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </Row>
        </Dialog>
    );
}

export default UpsertTaskDialog;

